import { useContext, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import { useAsyncFn } from 'react-use';

import { MonthlyPaymentViewEvent } from 'lib/Tracking/Srp/MonthlyPaymentView';
import { MEDIUM_RECTANGLE, LEADERBOARD } from 'lib/GoogleAds/Sizes';
import { X_SMALL, SMALL, LARGE, MEDIUM } from 'lib/DeviceScreen';
import { NoResultsEvent } from 'lib/Tracking/Srp/NoResultsEvent';
import { trackListingsView } from 'lib/Tracking/Srp/ResultsList';
import { fetchQuickPicks } from 'lib/AutolistAPI';

import QuickPicksEventContext from 'contexts/QuickPicksEventContext';
import QuickPicksModal from 'components/QuickPicks/Modal';
import MonthlyPaymentCalculator from 'components/Srp/MonthlyPaymentCalculator';
import QuickContactLeadForm from 'components/LeadForm/QuickContactLeadForm';
import SrpLeadForm from 'components/LeadForm/SrpLeadForm';
import SearchResultCard from 'components/Srp/SearchResultCard';
import SrpLoadingCard from 'components/Srp/LoadingCard';
import Pagination from 'components/Srp/Pagination';
import Ad from 'components/Ad';

import { VehicleV2 } from 'types/VehicleV2';
import SearchResponse from 'types/Apis/SearchResponse';
import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import SrpEventContext from 'contexts/SrpEventContext';

import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { SourcePages } from 'lib/EventSchema';
import useFavorite from 'lib/hooks/useFavorite';
import useFetchFavorites from 'lib/hooks/useFetchFavorites';
import useEvents from 'lib/hooks/useEvents';
import { useQuickContactLifecycle, useIsFirstUserLeadSubmitted } from 'lib/hooks/useQuickContact';

import { useQuickPicksAlwaysShown } from 'lib/hooks/useQuickPicksAlwaysShown';
import { AdGlobalRefreshContext } from 'contexts/AdGlobalRefreshContext';
import { LeadCtaClickEvent } from 'lib/Tracking/Srp/LeadCtaClickEvent';
import SrpTileCloseEvent from 'lib/Tracking/Srp/SrpTileCloseEvent';
import SrpTileViewEvent from 'lib/Tracking/Srp/SrpTileViewEvent';
import SrpTile from 'components/Srp/SrpTile';
import { useStyles } from './styles';
import NoResultsView from './NoResultsView';

export interface ResultsListProps {
  vehicles?: VehicleV2[];
  totalCount: number;
  isLoading?: boolean;
  vehicleDetails: VehicleInfo[];
  searchVehicles: () => void;
}

const targetingMap: { [key: number]: number } = {
  4: 5,
  9: 6,
  14: 7,
};

const adIndexes = [4, 9, 14];

const ResultsList = ({
  vehicles,
  totalCount,
  isLoading,
  vehicleDetails,
  searchVehicles,
}: ResultsListProps): JSX.Element => {
  const { isMobile } = useDeviceScreen();
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showMonthlyPaymentModal, setShowMonthlyPaymentModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState({} as VehicleV2);
  const [srpTileVehicle, setSrpTileVehicle] = useState<VehicleInfo>({} as VehicleInfo);
  const classes = useStyles();
  const logger = useEvents();
  const { sourcePage, sourcePageCategory } = useContext(SrpEventContext);
  const [isQuickPicksOpen, setIsQuickPicksOpen] = useState(false);
  const [isSrpTileModalOpen, setIsSrpTileModalOpen] = useState(false);
  const [quickPicksFetch, doFetchQuickPicks] = useAsyncFn(async (vin: string) => {
    return fetchQuickPicks(vin) as Promise<SearchResponse>;
  });

  const { refreshGlobalAds } = useContext(AdGlobalRefreshContext);

  useQuickContactLifecycle();
  const isFirstLeadSubmitted = useIsFirstUserLeadSubmitted();

  const quickPicksAlwaysShown = useQuickPicksAlwaysShown();

  const { favorite } = useFavorite(selectedVehicle.vin);
  useFetchFavorites();

  function showQuickPicks(vin: string): void {
    doFetchQuickPicks(vin);
    setIsQuickPicksOpen(true);
  }

  function handleLeadSubmitSuccess(submittedVehicle: VehicleV2): void {
    favorite();
    setShowLeadModal(false);

    if (submittedVehicle.isQuickPicksEligible || quickPicksAlwaysShown) {
      showQuickPicks(submittedVehicle.vin);
    }
  }

  function handleQuickLeadSuccess(submittedVehicle: VehicleV2): void {
    if (submittedVehicle.isQuickPicksEligible || quickPicksAlwaysShown) {
      showQuickPicks(submittedVehicle.vin);
    }
  }

  function handleCloseMonthlyPaymentDialog(): void {
    setShowMonthlyPaymentModal(false);
  }
  function handleCloseLeadDialog(): void {
    setShowLeadModal(false);
  }

  function onCloseSrpTile(): void {
    setIsSrpTileModalOpen(false);
    setSrpTileVehicle({} as VehicleInfo);
    logger.log(SrpTileCloseEvent());
  }

  function onMonthlyPaymentCalculatorClick(): void {
    logger.log(MonthlyPaymentViewEvent({ sourcePage }));
    setShowMonthlyPaymentModal(true);
  }

  function onOpenSrpTile(vin: string): void {
    const matchingVehicleDetails = vehicleDetails.find((vehicle) => vehicle.vin === vin);
    const matchingVehicleImages = vehicles?.find((vehicle) => vehicle.vin === vin)?.images ?? [];
    setSrpTileVehicle({
      ...matchingVehicleDetails,
      photoUrls: matchingVehicleImages,
    } as VehicleInfo);
    setIsSrpTileModalOpen(true);
    logger.log(SrpTileViewEvent());
  }

  const getContainerClass = (): string => {
    if (!vehicles?.length) {
      return classes.noResults;
    }
    if (sourcePage === SourcePages.SeoSrp) {
      return classes.fullWidth;
    }
    return classes.fixedHeight;
  };

  useEffect(() => {
    if (!isLoading && vehicles?.length === 0) logger.log(NoResultsEvent({ sourcePage }));
  }, [isLoading, vehicles, logger, sourcePage]);

  const vehiclesStr = JSON.stringify(vehicles);
  useEffect(() => {
    if (vehicles?.length) {
      const searchString = window.location.hash;
      const vins = vehicles?.map((v: VehicleV2) => v.vin) ?? [];
      if (!searchString.includes('vin=')) {
        trackListingsView(vins, searchString);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesStr, totalCount]);

  const renderResults = (): JSX.Element | JSX.Element[] =>
    vehicles?.length ? (
      vehicles.map((vehicle, i) => (
        <div key={i} className={classes.item}>
          <SearchResultCard
            prioritizeImage={i === 0}
            vehicle={vehicle}
            onClickContact={(): void => {
              logger.log(LeadCtaClickEvent({ sourcePage }));
              setSelectedVehicle(vehicle);
              setShowLeadModal(true);
            }}
            onQuickLeadSucceeded={(): void => handleQuickLeadSuccess(vehicle)}
            onClickMonthlyPaymentCalculator={onMonthlyPaymentCalculatorClick}
            onOpenSrpTile={onOpenSrpTile}
          />
          <div className={classes.searchResultSpacer} />
          {adIndexes.includes(i) ? (
            <>
              <Ad
                adConfig={{
                  dimensions: [[728, 90]],
                  targeting: { inLine: targetingMap[i] },
                  screenSizes: [SMALL, MEDIUM, LARGE],
                }}
                placeholderSize={LEADERBOARD}
                className={classes.desktopInlineAad}
              />
              <Ad
                adConfig={{
                  dimensions: [[300, 250]],
                  targeting: { inLine: targetingMap[i] },
                  screenSizes: [X_SMALL],
                }}
                placeholderSize={MEDIUM_RECTANGLE}
                className={classes.mobileInlineAad}
              />
              <div className={classes.searchResultSpacer} />
            </>
          ) : null}
        </div>
      ))
    ) : (
      <NoResultsView />
    );

  if (isLoading) {
    return (
      <>
        <div
          className={sourcePage === SourcePages.SeoSrp ? classes.fullWidth : classes.fixedHeight}
        >
          <div className={classes.resultsList}>
            {[...Array(10)].map((_, i) => (
              <div key={i} className={classes.item}>
                <SrpLoadingCard />
                <div className={classes.searchResultSpacer} />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={getContainerClass()}>
        <div className={classes.resultsList}>{renderResults()}</div>
        <div className={classes.pagination}>
          <Pagination
            resultsCount={totalCount}
            onPageChange={(): void => {
              refreshGlobalAds();
              window.scrollTo({ top: 0 });
              if (isMobile) {
                searchVehicles();
              }
            }}
          />
        </div>
      </div>
      <Dialog
        fullScreen
        disableScrollLock
        open={showLeadModal}
        onClose={handleCloseLeadDialog}
        PaperProps={{
          classes: {
            root: classes.dialog,
          },
        }}
      >
        {isFirstLeadSubmitted ? (
          <QuickContactLeadForm
            selectedVehicle={selectedVehicle}
            onClose={handleCloseLeadDialog}
            onSubmitSuccess={handleLeadSubmitSuccess}
          />
        ) : (
          <SrpLeadForm
            selectedVehicle={selectedVehicle}
            onClose={handleCloseLeadDialog}
            onSubmitSuccess={handleLeadSubmitSuccess}
          />
        )}
      </Dialog>
      <Dialog
        fullScreen
        disableScrollLock
        open={showMonthlyPaymentModal}
        onClose={handleCloseMonthlyPaymentDialog}
        PaperProps={{
          classes: {
            root: classes.monthlyPaymentDialog,
          },
        }}
      >
        <MonthlyPaymentCalculator onClick={handleCloseMonthlyPaymentDialog} />
      </Dialog>
      <QuickPicksEventContext.Provider
        value={{
          leadTrackingSourceDesktop:
            sourcePage === SourcePages.SeoSrp
              ? LeadTrackingPageSource.DesktopSeoSrpQuickPicks
              : LeadTrackingPageSource.DesktopSrpQuickPicks,
          leadTrackingSourceMobile:
            sourcePage === SourcePages.SeoSrp
              ? LeadTrackingPageSource.MobileSeoSrpQuickPicks
              : LeadTrackingPageSource.MobileSrpQuickPicks,
          sourceQP:
            sourcePage === SourcePages.SeoSrp
              ? SourcePages.SeoSrpQuickPicks
              : SourcePages.SrpQuickPicks,
          sourcePage: sourcePage === SourcePages.SeoSrp ? SourcePages.SeoSrp : SourcePages.Srp,
          sourcePageCategory: sourcePageCategory || sourcePage,
        }}
      >
        <QuickPicksModal
          vehicles={quickPicksFetch.value?.records || []}
          isLoading={quickPicksFetch.loading}
          close={(): void => {
            setIsQuickPicksOpen(false);
          }}
          isOpen={isQuickPicksOpen}
        />
      </QuickPicksEventContext.Provider>
      <SrpTile isModalOpen={isSrpTileModalOpen} vehicle={srpTileVehicle} onClose={onCloseSrpTile} />
    </>
  );
};

export default ResultsList;
